import PropTypes from 'prop-types';
import React from 'react';
import axios from 'axios';

import { Badge, Button, Menu, Tooltip, Copy01, copyText, DotsGrid, DotsVertical, Edit01, Link01, Trash01 } from '@opendate-app/setlist';
import { errorText, toastError, toastSuccess } from '../../../shared/toastHelper';
import TicketTypeInventoryStats from '../../../shared/TicketTypeInventoryStats';
import DemandBadge from '../../shared/components/DemandBadge';
import DynamicPriceDisplay from '../../shared/components/DynamicPriceDisplay';
import { salesWindowInverted, INVERTED_WINDOW_ADD_ON_WARNING, TIMED_ENTRY_UNUSABLE_ADD_ON_WARNING } from '../../shared/utils/showDetailWarnings';

export const draggablePropsWithStyle = (provided, snapshot) => {
  var props =  {...provided.draggableProps};
  var style = Object.assign({}, props.style, {
    display: (snapshot.isDragging ? "table" : ""),
    background: "var(--sl-bg-primary)",
    borderWidth: snapshot.isDragging ? "1px" : "0 0 1px 0",
    borderColor: "var(--sl-border-secondary)",
    borderStyle: "solid"
  });

  return Object.assign({}, props, {style: style});
}

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const addOnStartTime = (addOn) => {
  if (!addOn.start_time || !addOn.end_time) {
    return "Unscheduled";
  }

  var startTimePieces = [];

  if(addOn.scheduled){
    startTimePieces.push("Scheduled");
  }

  startTimePieces.push(addOn.human_start_time);
  return startTimePieces.join(" • ");
}

const copyTableSaleLink = (csrfToken, team, confirm, addOn) => {
  const copied = copyText(`${confirm.public_ticketing_url}/upgrade`);
  axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

  return Promise.all([copied, axios.post(`/teams/${team.id}/add_ons/${addOn.id}/table_sale_link`, {})])
    .then(([ok]) => {
      if (!ok) throw new Error('copy failed');
      toastSuccess("Link copied. Fans can buy a table with it without a GA ticket. Anyone with the show's link can use it.");
    })
    .catch((e) => toastError(errorText(e, "Couldn't copy the table-sale link. Try again.")));
};

const AddOnRow = ({
  csrfToken,
  team,
  currentUser,
  confirm,
  fetchNewAddOn,
  fetchAddOnToEdit,
  deleteAddOn,
  addOn,
  provided,
  snapshot,
  addOnIsSeated,
  setDuplicatingAddOnId,
  addOns,
  isSharingInventory
}) => {
  const overflowActions = [
    {
      label: 'Edit',
      icon: Edit01,
      onClick: () => fetchAddOnToEdit(csrfToken, team, addOn),
    },
    ...(!addOnIsSeated(addOn) ? [{
      label: 'Copy',
      icon: Copy01,
      onClick: () => fetchNewAddOn(csrfToken, team, confirm, addOn),
    }] : !addOn.seating_chart_event_id ? [{
      label: 'Duplicate',
      icon: Copy01,
      onClick: () => setDuplicatingAddOnId(addOn.id),
    }] : []),
    ...(addOnIsSeated(addOn) ? [{
      label: 'Copy table-sale link',
      icon: Link01,
      onClick: () => copyTableSaleLink(csrfToken, team, confirm, addOn),
    }] : []),
    ...(addOn.number_of_tickets_sold === 0 && (!addOnIsSeated(addOn) || addOn.share_inventory_with_id) ? [{
      label: 'Delete',
      icon: Trash01,
      destructive: true,
      onClick: () => deleteAddOn(csrfToken, team, addOn),
    }] : []),
  ];

  return (
  <tr ref={provided.innerRef} {...draggablePropsWithStyle(provided, snapshot)}>
    <td
      data-label=""
      className="px-1 text-center d-none d-md-table-cell"
      style={{verticalAlign: "middle"}}
    >
      <a {...provided.dragHandleProps}
         onClick={
           (e) => {
             e.preventDefault();
           }
         }
         href="#"
         aria-label="Reorder add-on"
         style={{
           "opacity": (addOnIsSeated(addOn) ? "0" : "1"),
           color: "var(--sl-text-disabled)"
         }}
         className={"py-0 btn btn-link btn-sm small " + (addOnIsSeated(addOn) ? "disabled" : "")}>
        <DotsGrid width={16} height={16} aria-hidden="true" />
      </a>
    </td>
    <td data-label="Ticket Name" className="overflow-ellipsis">
      <div>
        <Tooltip title={`${addOn.base_name}${isSharingInventory(addOns, addOn) ? " • Shared Inventory" : ""}`}>
          <span>
            <strong>
              {addOn.base_name}
            </strong>
            {isSharingInventory(addOns, addOn) ? (
              <span style={{"marginLeft": "5px", color: "var(--sl-text-brand)"}}>
                Shared Inventory
              </span>
            ) : null}
          </span>
        </Tooltip>
        {addOn.dynamic_pricing_enabled && (
          <span className="ml-2">
            <Badge color="blue" size="sm">⚡ Dynamic</Badge>
          </span>
        )}
        <DemandBadge enabled={addOn.dynamic_pricing_enabled} demandStatus={confirm.demand_status} />
      </div>
      {addOn.price_points.map((pricePoint, index) => (
        <div key={index} style={{marginTop: "12px", fontSize: "12px"}}>
          <span style={{marginRight: "4px"}}>
            <Badge color="brand" size="sm">Price Change {index + 1}</Badge>
          </span>
          {pricePoint.name}
        </div>
      ))}
    </td>
    <td data-label="On Sale Date">
      <div>
        <span style={(!addOn.start_time || !addOn.end_time || salesWindowInverted(addOn)) ? {color: "var(--sl-text-error)"} : null}
              title={addOnStartTime(addOn)}>
          {addOnStartTime(addOn)}
          {salesWindowInverted(addOn) ? (
            <i className="fas fa-triangle-exclamation small ml-2"
               role="img"
               aria-label={INVERTED_WINDOW_ADD_ON_WARNING}
               title={INVERTED_WINDOW_ADD_ON_WARNING}></i>
          ) : null}
          {addOn.timed_entry_window_unusable ? (
            <i className="fas fa-clock small ml-2"
               style={{color: "var(--sl-text-error)"}}
               role="img"
               aria-label={TIMED_ENTRY_UNUSABLE_ADD_ON_WARNING}
               title={TIMED_ENTRY_UNUSABLE_ADD_ON_WARNING}></i>
          ) : null}
        </span>
      </div>
      {addOn.price_points.map((pricePoint, index) => (
        <div key={index} style={{marginTop: "12px", fontSize: "12px"}}>
          {pricePoint.human_starts_at}
        </div>
      ))}
    </td>
    <td data-label="Price">
      <div>
        <DynamicPriceDisplay
          enabled={addOn.dynamic_pricing_enabled}
          currentPrice={addOn.dynamic_pricing_current_price}
          basePrice={addOn.base_price}
          formatter={formatter}
          fallback={formatter.format(addOn.base_price)}
        />
      </div>
      {addOn.price_points.map((pricePoint, index) => (
        <div key={index} style={{marginTop: "12px", fontSize: "12px"}}>
          {formatter.format(pricePoint.price)}
        </div>
      ))}
    </td>
    <td data-label="Inventory">
      <div>
        {addOn.available}
      </div>
      <TicketTypeInventoryStats item={addOn} />
    </td>
    <td
      data-label=""
      className="text-center"
      style={{verticalAlign: "middle"}}
    >
      <Menu
        placement="bottom-end"
        items={overflowActions}
        trigger={(
          <Button
            variant="tertiary"
            size="sm"
            icon={DotsVertical}
            aria-label={`Actions for ${addOn.base_name}`}
          />
        )}
      />
    </td>
  </tr>
  );
};

AddOnRow.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  fetchNewAddOn: PropTypes.func.isRequired,
  fetchAddOnToEdit: PropTypes.func.isRequired,
  deleteAddOn: PropTypes.func.isRequired,
  addOn: PropTypes.object,
  provided: PropTypes.object.isRequired,
  snapshot: PropTypes.object.isRequired,
  addOnIsSeated: PropTypes.func.isRequired,
  setDuplicatingAddOnId: PropTypes.func.isRequired,
  addOns: PropTypes.array,
  isSharingInventory: PropTypes.func.isRequired
};

export default AddOnRow;
